import {useSearchParams, type To} from '@github-ui/react-router'
import {pullRequestsOverviewIndexRoute} from '../../../routes/overview-route'
import {issueCommentAnchor, reviewSummaryAnchor} from '../../comments/utils/comment-anchors'
import {ViewMode, ViewModeSchema} from '../../comments/CommentsContext'
import type {SetStateAction} from 'react'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

type PullRequestPathParams = Parameters<typeof pullRequestsOverviewIndexRoute.generatePath>[0]

const viewModeSearchParam = 'comments:mode'

function routeToItem(pathParams: PullRequestPathParams, anchor: string): To {
  return {
    pathname: pullRequestsOverviewIndexRoute.generatePath(pathParams),
    hash: anchor,
    // Empty search resets the issues view mode to go back to "all", ensuring the comment is visible
    search: '',
  }
}

/** Build router `To` object for linking to an issue comment on the Overview page. */
export function routeToIssueComment(pathParams: PullRequestPathParams, commentId: string) {
  return routeToItem(pathParams, issueCommentAnchor(commentId))
}

/** Build router `To` object for linking to a review summary on the Overview page. */
export function routeToReviewSummary(pathParams: PullRequestPathParams, reviewId: string) {
  return routeToItem(pathParams, reviewSummaryAnchor(reviewId))
}

const ViewModeValidator = Compile(ViewModeSchema)

function parseViewModeOrNull(value: string | null) {
  return value !== null && ViewModeValidator.Check(value) ? value : null
}

/** Get the view mode state and setter, using URL query params as the source of truth. */
export function useViewModeFromSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const viewModeParam = searchParams.get(viewModeSearchParam)
  const viewMode = parseViewModeOrNull(viewModeParam) ?? ViewMode.IssueComments

  const setViewMode = (newViewMode: SetStateAction<ViewMode>) =>
    setSearchParams(params => {
      // We use no param to represent the "all" view. This keeps the page URL tidy
      if (newViewMode === ViewMode.IssueComments) params.delete(viewModeSearchParam)
      else params.set(viewModeSearchParam, typeof newViewMode === 'function' ? newViewMode(viewMode) : newViewMode)

      return params
    })

  return [viewMode, setViewMode] as const
}
